import { clamp01, hash1, smoothstep } from "../math.js";

/** Where the box cuts off the artwork, the dots shrink away instead of stopping at a straight
    line.

    Along the bottom edge (the shirt; on screen, the right side of the turned portrait), the
    shrink depth tapers from `start` at artwork x = `span`, near the top of the visible
    portrait, to `end` at x = 0. It deepens late (`power`), so the shirt keeps its width and
    then ends in a point near the bottom. `roughness` is how far the noise reaches.

    The other edges shrink over 0.35 to 1 times `edgeMargin`, depending on the noise. A dot
    that would drop below `minKeep` of its size disappears entirely. All in artwork units.
    The page reads these too, for the face's outline, and the portrait asset script bakes the
    X-ray's edge mask from `faceEdgeKeep`. */
export const FACE_EDGE = Object.freeze({
  edgeMargin: 70,
  span: 600,
  start: 15,
  end: 380,
  power: 2.5,
  roughness: 30,
  minKeep: 0.25,
});

/** The artwork's side, in artwork units. */
const ARTWORK_SIZE = 1024;

function valueNoise(t: number): number {
  const i = Math.floor(t);
  const f = t - i;
  return hash1(i) + (hash1(i + 1) - hash1(i)) * f * f * (3 - 2 * f);
}
// Smooth noise in [0, 1] along an edge: three octaves.
const edgeNoise = (t: number): number =>
  0.5 * valueNoise(t) + 0.3 * valueNoise(t * 2.3 + 17) + 0.2 * valueNoise(t * 5.1 + 41);

/** How much of its size the shrinking edge leaves a dot at artwork point (x, y), as a fraction:
    1 inside, 0 on the box's edge. The face's vertex shader runs the same function
    (`FACE_EDGE_GLSL`); the GPU's float precision can nudge the noise a little. */
export function faceEdgeKeep(x: number, y: number): number {
  const { edgeMargin, span, start, end, power, roughness } = FACE_EDGE;
  const along = clamp01(1 - x / span);
  const taper =
    start + (end - start) * along ** power + roughness * (edgeNoise(x / 45 + 3.1) - 0.5) * 2;
  let keep = smoothstep(0, 1, (ARTWORK_SIZE - y) / Math.max(taper, 1));
  // Left, right, and top: the distance to the edge, and the position along it.
  const sides: readonly (readonly [number, number])[] = [
    [x, y],
    [ARTWORK_SIZE - x, y],
    [y, x],
  ];
  sides.forEach(([distance, position], side) => {
    const depth = edgeMargin * (0.35 + 0.65 * edgeNoise(position / 45 + side * 7.3));
    keep = Math.min(keep, smoothstep(0, 1, distance / depth));
  });
  return keep;
}

const glslFloat = (n: number): string => n.toFixed(2);

/** `faceEdgeKeep()` in GLSL, for the face's vertex shader. */
export const FACE_EDGE_GLSL = `
const float EDGE_MARGIN = ${glslFloat(FACE_EDGE.edgeMargin)};
const float TAPER_SPAN = ${glslFloat(FACE_EDGE.span)};
const float TAPER_START = ${glslFloat(FACE_EDGE.start)};
const float TAPER_END = ${glslFloat(FACE_EDGE.end)};
const float TAPER_POWER = ${glslFloat(FACE_EDGE.power)};
const float TAPER_ROUGHNESS = ${glslFloat(FACE_EDGE.roughness)};
const float ARTWORK_SIZE = ${glslFloat(ARTWORK_SIZE)};
float hash1(float n) {
  uint x = uint(int(n));
  x = (x ^ (x >> 16u)) * 0x7feb352du;
  x = (x ^ (x >> 15u)) * 0x846ca68bu;
  return float(x ^ (x >> 16u)) / 4294967296.0;
}
float valueNoise(float t) {
  float i = floor(t);
  float f = fract(t);
  return mix(hash1(i), hash1(i + 1.0), f * f * (3.0 - 2.0 * f));
}
float edgeNoise(float t) {
  return 0.5 * valueNoise(t) + 0.3 * valueNoise(t * 2.3 + 17.0) + 0.2 * valueNoise(t * 5.1 + 41.0);
}
float faceEdgeKeep(vec2 p) {
  float along = clamp(1.0 - p.x / TAPER_SPAN, 0.0, 1.0);
  float taper = mix(TAPER_START, TAPER_END, pow(along, TAPER_POWER))
    + TAPER_ROUGHNESS * (edgeNoise(p.x / 45.0 + 3.1) - 0.5) * 2.0;
  float keep = smoothstep(0.0, 1.0, (ARTWORK_SIZE - p.y) / max(taper, 1.0));
  vec3 sideDistance = vec3(p.x, ARTWORK_SIZE - p.x, p.y);
  vec3 sidePosition = vec3(p.y, p.y, p.x);
  for (int side = 0; side < 3; side++) {
    float depth = EDGE_MARGIN * (0.35 + 0.65 * edgeNoise(sidePosition[side] / 45.0 + float(side) * 7.3));
    keep = min(keep, smoothstep(0.0, 1.0, sideDistance[side] / depth));
  }
  return keep;
}
`;
